import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  FileEdit,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Download,
  Upload,
} from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/empty-state'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  fetchFormGroups,
  deleteFormGroup,
  deleteForm,
  type FormGroup,
} from '../api/form-builder-api'
import { FormGroupModal } from '../components/form-group-modal'
import { CreateFormModal } from '../components/create-form-modal'
import { ExportDialog, type ExportTarget } from '../components/export-dialog'
import { ImportDialog } from '../components/import-dialog'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { cn } from '@/lib/utils/cn'

export function FormBuilderHome() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState<FormGroup | null>(null)
  const [createFormForGroup, setCreateFormForGroup] = useState<string | null>(null)
  const [deletingGroup, setDeletingGroup] = useState<FormGroup | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [exportTarget, setExportTarget] = useState<ExportTarget | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['form-groups', projectId],
    queryFn: () => fetchFormGroups(projectId!),
    enabled: !!projectId,
  })

  const deleteGroupMutation = useMutation({
    mutationFn: () => deleteFormGroup(deletingGroup!.id, user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['form-groups', projectId] })
      setDeletingGroup(null)
    },
  })

  const deleteFormMutation = useMutation({
    mutationFn: (formId: string) => deleteForm(formId, user!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['form-groups', projectId] }),
  })

  const toggleGroup = (id: string) => {
    const next = new Set(expandedGroups)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpandedGroups(next)
  }

  const openGroupEdit = (g: FormGroup) => {
    setEditingGroup(g)
    setGroupModalOpen(true)
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => setImportOpen(true)}
      >
        <Upload className="w-4 h-4" />
        ورود فرم
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        disabled={groups.length === 0}
        onClick={() =>
          setExportTarget({ projectId: projectId!, scope: 'project', label: 'کل پروژه' })
        }
      >
        <Download className="w-4 h-4" />
        خروجی پروژه
      </Button>
      <Button
        size="sm"
        className="gap-1.5"
        onClick={() => { setEditingGroup(null); setGroupModalOpen(true) }}
      >
        <Plus className="w-4 h-4" />
        ایجاد گروه
      </Button>
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="فرم‌ساز"
        breadcrumbs={[{ label: 'فرم‌ساز' }]}
        actions={actions}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
          ))
        ) : groups.length === 0 ? (
          <EmptyState
            icon={FileEdit}
            title="هنوز گروه فرمی تعریف نشده"
            description="با ایجاد گروه اول، ساختار فرم‌های پروژه را بچینید"
            action={
              <Button
                size="sm"
                onClick={() => { setEditingGroup(null); setGroupModalOpen(true) }}
              >
                <Plus className="w-4 h-4 me-1.5" />
                ایجاد گروه اول
              </Button>
            }
          />
        ) : (
          groups.map((group) => {
            const isExpanded = expandedGroups.has(group.id)
            return (
              <div key={group.id} className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Group header */}
                <div className="flex items-center gap-3 px-5 py-4">
                  <button
                    className="flex items-center gap-3 flex-1 text-start hover:opacity-80 transition-opacity"
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center bg-muted/40 text-foreground shrink-0">
                      <DynamicIcon name={group.icon} className="w-5 h-5" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-foreground">{group.title}</p>
                      {group.description && (
                        <p className="text-xs text-muted-foreground truncate">{group.description}</p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground me-2 shrink-0">
                      {group.forms.length} فرم
                    </span>
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 rotate-180" />
                    }
                  </button>

                  {/* Group actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => setCreateFormForGroup(group.id)}
                    >
                      <Plus className="w-3 h-3" />
                      فرم جدید
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openGroupEdit(group)}>
                          <Pencil className="w-4 h-4 me-2" />
                          ویرایش گروه
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            setExportTarget({
                              projectId: projectId!,
                              scope: 'group',
                              groupId: group.id,
                              label: group.title,
                            })
                          }
                        >
                          <Download className="w-4 h-4 me-2" />
                          خروجی گروه
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          disabled={group.forms.length > 0}
                          onClick={() => setDeletingGroup(group)}
                        >
                          <Trash2 className="w-4 h-4 me-2" />
                          حذف گروه
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Forms list */}
                {isExpanded && group.forms.length > 0 && (
                  <div className="border-t border-border divide-y divide-border">
                    {group.forms.map((form) => (
                      <div
                        key={form.id}
                        className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/20 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-md flex items-center justify-center bg-muted/30 text-muted-foreground shrink-0">
                          <DynamicIcon name={form.icon} className="w-4 h-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">
                              {form.title}
                            </p>
                            <span className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
                              form.isPublished
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : 'bg-yellow-500/15 text-yellow-400'
                            )}>
                              {form.isPublished ? 'منتشر شده' : 'پیش‌نویس'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                              {form.instanceType === 'single' ? 'تک‌نمونه' : 'تکرارپذیر'}
                            </span>
                          </div>
                          <p className="text-[11px] font-mono text-muted-foreground mt-0.5">
                            {form.formKey}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs gap-1"
                            onClick={() =>
                              navigate(`/projects/${projectId}/form-builder/${form.id}`)
                            }
                          >
                            <Pencil className="w-3 h-3" />
                            ویرایش
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  setExportTarget({
                                    projectId: projectId!,
                                    scope: 'form',
                                    formId: form.id,
                                    label: form.title,
                                  })
                                }
                              >
                                <Download className="w-4 h-4 me-2" />
                                خروجی فرم
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => deleteFormMutation.mutate(form.id)}
                              >
                                <Trash2 className="w-4 h-4 me-2" />
                                حذف فرم
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isExpanded && group.forms.length === 0 && (
                  <div className="border-t border-border px-5 py-8 text-center">
                    <p className="text-sm text-muted-foreground mb-3">هنوز فرمی در این گروه نیست</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateFormForGroup(group.id)}
                    >
                      <Plus className="w-4 h-4 me-1.5" />
                      افزودن فرم
                    </Button>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>

      {/* Modals */}
      {projectId && (
        <>
          <FormGroupModal
            projectId={projectId}
            group={editingGroup}
            open={groupModalOpen}
            onClose={() => { setGroupModalOpen(false); setEditingGroup(null) }}
          />

          {createFormForGroup && (
            <CreateFormModal
              projectId={projectId}
              groupId={createFormForGroup}
              open={!!createFormForGroup}
              onClose={() => setCreateFormForGroup(null)}
            />
          )}

          <Dialog open={!!deletingGroup} onOpenChange={(o) => !o && setDeletingGroup(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>حذف گروه فرم؟</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                گروه «{deletingGroup?.title}» حذف می‌شود. این عمل قابل بازگشت نیست.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeletingGroup(null)}>انصراف</Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteGroupMutation.mutate()}
                  disabled={deleteGroupMutation.isPending}
                >
                  حذف گروه
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <ExportDialog
            target={exportTarget}
            open={!!exportTarget}
            onClose={() => setExportTarget(null)}
          />

          <ImportDialog
            projectId={projectId}
            existingGroups={groups.map((g) => ({ id: g.id, title: g.title }))}
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={() => qc.invalidateQueries({ queryKey: ['form-groups', projectId] })}
          />
        </>
      )}
    </div>
  )
}
